import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Typography,
  MenuItem
} from "@mui/material";
import { get, put } from "src/services/httpClient.service";
import { useSnackbar } from "src/components/snackbar/useSnackbar";

/* =========================
   Types
========================= */
interface Vendor {
  vendorId: string;
  vendorName: string;
}

interface CityDropdown {
  id: number;
  city: string;
}

interface UpdateAreaProps {
  handleClose: () => void;
  fetchAreas: () => void;
  areaidvalues: any;
  selectedVendorId: string; // Add this prop
}

const BoardUpdateArea: React.FC<UpdateAreaProps> = ({
  handleClose,
  fetchAreas,
  areaidvalues,
  selectedVendorId // Receive the selected vendor ID
}) => {
  const APIURL = process.env.REACT_APP_BASE_API_URL;
  const { showSnackbar } = useSnackbar();

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [cities, setCities] = useState<CityDropdown[]>([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    id: 0,
    vendorId: selectedVendorId || "", // Initialize with passed vendor ID
    cityId: "",
    name: "",
    // zipCode: "",
    isActive: true
  });

  /* =========================
     Fetch Vendors (for display purposes only)
  ========================= */
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const res = await get(`${APIURL}/api/vendors`);
        setVendors(res?.data || res);
      } catch {
        showSnackbar("Failed to load vendors", "error");
      }
    };

    fetchVendors();
  }, [APIURL, showSnackbar]);

  /* =========================
     Fetch Cities (Vendor Based)
  ========================= */
  useEffect(() => {
    const fetchCities = async () => {
      const vendorIdToUse = selectedVendorId || form.vendorId;
      if (!vendorIdToUse) {
        setCities([]);
        return;
      }

      try {
        const response = await get(
          `${APIURL}/api/miscellaneous/city/vendor/${vendorIdToUse}/findAll`
        );
        setCities(response?.data || response || []);
      } catch {
        showSnackbar("Failed to load cities", "error");
      }
    };

    fetchCities();
  }, [APIURL, selectedVendorId, form.vendorId, showSnackbar]);

  /* =========================
     Prefill Area Data
  ========================= */
  useEffect(() => {
    if (areaidvalues) {
      setForm({
        id: areaidvalues.id,
        vendorId: selectedVendorId || areaidvalues.vendorId || "", // Use selectedVendorId if available
        cityId: areaidvalues.cityId || "",
        name: areaidvalues.name || "",
        // zipCode: areaidvalues.zipCode || "",
        isActive: areaidvalues.isActive
      });
    }
  }, [areaidvalues, selectedVendorId]);

  /* =========================
     Handlers
  ========================= */
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!form.vendorId || !form.cityId || !form.name ) {
      showSnackbar(
        "Vendor, City, Area Name and Zip Code are required",
        "warning"
      );
      return;
    }

    setLoading(true);

    try {
      const payload = {
        id: form.id,
        vendorId: form.vendorId,
        cityId: Number(form.cityId),
        name: form.name,
        // zipCode: form.zipCode,
        isActive: form.isActive
      };

      const response = await put(
        `${APIURL}/api/miscellaneous/area`,
        payload
      );

      if (response?.statusCode === 200) {
        showSnackbar(
          response?.message || "Area updated successfully",
          "success"
        );
        fetchAreas();
        handleClose();
      } else {
        showSnackbar(
          response?.message || "Failed to update area",
          "error"
        );
      }
    } catch (error: any) {
      showSnackbar(error?.message || "Failed to update area", "error");
    } finally {
      setLoading(false);
    }
  };

  // Find the selected vendor name for display
  const selectedVendor = vendors.find(v => v.vendorId === form.vendorId);

  /* =========================
     UI
  ========================= */
  return (
    <Box p={3}>
      <Typography variant="h6" mb={2}>
        Update Area
      </Typography>

      <Grid container spacing={2}>
        {/* Vendor Display - Read Only */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="Vendor"
            value={selectedVendor?.vendorName || form.vendorId || ""}
            disabled
            InputProps={{
              readOnly: true,
            }}
            helperText="Vendor is selected from the parent list"
          />
          {/* Hidden fields for submission */}
          <input type="hidden" name="vendorId" value={form.vendorId} />
          <input type="hidden" name="id" value={form.id} />
        </Grid>

        {/* City Dropdown */}
        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            required
            label="City"
            name="cityId"
            value={form.cityId}
            onChange={handleChange}
          >
            <MenuItem value="" disabled>
              Select City
            </MenuItem>
            {cities.map(city => (
              <MenuItem key={city.id} value={city.id}>
                {city.city}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Area Name */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="Area Name"
            name="name"
            value={form.name}
            onChange={handleChange}
          />
        </Grid>

        {/* Zip Code */}
        {/* <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="Zip Code"
            name="zipCode"
            value={form.zipCode}
            onChange={handleChange}
          />
        </Grid> */}

        {/* Active Switch */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                onChange={e =>
                  setForm(prev => ({
                    ...prev,
                    isActive: e.target.checked
                  }))
                }
              />
            }
            label="Is Active"
          />
        </Grid>

        {/* Actions */}
        <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
          >
            Update
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BoardUpdateArea;